import React, {useState} from 'react';
import PropTypes from 'prop-types';

/**
 * How many options are shown before the rest are folded away.
 *
 * A reply that offers everything it can do offers a great deal - declining
 * an out-of-scope request listed thirty-three chips under one sentence,
 * which is a wall to scroll past rather than an answer. Five is about what
 * reads as a short list at a glance; the rest are one click away and the
 * numbering does not shift when they arrive, so typing a number still picks
 * what it always picked.
 */
export const VISIBLE_CHIPS = 5;

// A disambiguation reply's own chip labels sometimes already read "1. switch",
// "2. switchper" - the backend numbers them because a plain-text reply like
// "2" needs a matching option to resend. The chip renders its own index badge
// beside the label regardless, so showing that leading number a second time
// inside the label reads as "1  1. switch". Strip it for display only - the
// raw label (with its number intact) is still what gets sent back on click.
const LEADING_INDEX_PATTERN = /^\d+\.\s*/;

/**
 * A chip label with any redundant leading "N. " stripped for display.
 * @param {string} text the raw label
 * @return {string} the label without its own leading ordinal
 */
function displayLabel(text) {
	return typeof text === 'string' ? text.replace(LEADING_INDEX_PATTERN, '') : text;
}

/**
 * The options offered beside a reply, shown a few at a time.
 * @param {object} props component props
 * @param {Array} props.items options to offer, in order
 * @param {boolean} props.disabled whether this offer has already been answered
 * @param {Function} props.onSelect called with (item, index) when one is picked
 * @param {Function} props.keyFor React key for one item
 * @param {Function} props.labelFor the words shown on one item
 * @return {JSX.Element} the chip row
 */
function OfferChips({items, disabled, onSelect, keyFor, labelFor}) {
	const [expanded, setExpanded] = useState(false);
	const hidden = items.length - VISIBLE_CHIPS;
	const shown = expanded || hidden <= 0
		? items
		: items.slice(0, VISIBLE_CHIPS);

	return (
		<div className="ai-chat-panel__offer-actions ai-chat-panel__offer-actions--multi">
			{shown.map((item, i) => (
				<button
					key={keyFor(item, i)}
					type="button"
					className={
						'ai-chat-panel__offer-btn ai-chat-panel__offer-chip' +
						(disabled ? ' ai-chat-panel__offer-btn--disabled' : '')
					}
					onClick={() => onSelect(item, i)}
					disabled={disabled}
					title={displayLabel(labelFor(item))}
				>
					<span className="ai-chat-panel__offer-index">{i + 1}</span>
					{displayLabel(labelFor(item))}
				</button>
			))}
			{hidden > 0 && (
				// Never disabled with the rest: an offer already answered can
				// still be read, and hiding what was on it helps nobody.
				<button
					type="button"
					className={
						'ai-chat-panel__offer-btn ai-chat-panel__offer-chip ' +
						'ai-chat-panel__offer-chip--more'
					}
					onClick={() => setExpanded((was) => !was)}
					aria-expanded={expanded}
				>
					{expanded ? 'Show fewer' : `See ${hidden} more`}
				</button>
			)}
		</div>
	);
}

OfferChips.propTypes = {
	items: PropTypes.array.isRequired,
	disabled: PropTypes.bool,
	onSelect: PropTypes.func.isRequired,
	keyFor: PropTypes.func.isRequired,
	labelFor: PropTypes.func.isRequired,
};

OfferChips.defaultProps = {
	disabled: false,
};

export default OfferChips;
